import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { Avatar } from "./Avatar";
import { FloatingActionManager } from "./floating/FloatingActionManager";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useExperienceMode } from "../hooks/useExperienceMode";
import { NotificationBell } from "./NotificationBell";
import { GlassQuickPanel } from "./GlassQuickPanel";
import { ThemeCycleButton } from "./ThemeToggle";
import { InstallPrompt } from "./InstallPrompt";
import { userCanAccessFounderConsole } from "./navItems";

// Release-review UI fix — admin navigation renders ONLY from the
// server-issued `is_admin` flag (/api/auth/me, role-based). No hardcoded
// founder emails in the frontend, ever.
const isAdminUser = userCanAccessFounderConsole;

const SECTIONS = [
  { key: "home",    label: "Home",    path: "/home",           icon: "home" },
  { key: "closet",  label: "Closet",  path: "/closet",         icon: "closet" },
  { key: "stylist", label: "Stylist", path: "/stylist",        icon: "stylist" },
  { key: "social",  label: "Social",  path: "/social",         icon: "social" },
  { key: "profile", label: "Profile", path: "/profile",        icon: "profile" },
];

const navOpts = (current, target) => ({ replace: current === target });

const isActive = (path, location) => {
  if (path === "/") return location.pathname === "/";
  if (path === "/admin/dashboard") return location.pathname.startsWith("/admin");
  return location.pathname === path;
};

const Glyph = ({ name, active }) => {
  const color = active ? "#0D0D0D" : "#8E8A84";
  switch (name) {
    case "home":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      );
    case "stylist":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M8 9h8M8 13h6" opacity="0.4" />
        </svg>
      );
    case "closet":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18" opacity="0.4" />
          <path d="M9 21V9" opacity="0.4" />
        </svg>
      );
    case "social":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "profile":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "services":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "admin":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case "camera":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      );
    case "search":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
      );
    case "shop":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <path d="M3 7h18l-1.5 12a2 2 0 0 1-2 1.7H6.5a2 2 0 0 1-2-1.7L3 7z" />
          <path d="M8 7a4 4 0 0 1 8 0" />
        </svg>
      );
    case "portfolio":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      );
    case "grid":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    default: return null;
  }
};

const AdminSubNav = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = isAdminUser(user);

  if (!isAdmin || !location.pathname.startsWith("/admin")) return null;

  const links = [
    { key: "dashboard",  path: "/admin/dashboard",         label: "Dashboard" },
    { key: "ops",        path: "/admin/ops",               label: "Ops Readiness" },
    { key: "beta",       path: "/admin/beta-access",       label: "Beta Access" },
    { key: "users",      path: "/admin/users",             label: "Users" },
    { key: "feedback",   path: "/admin/feedback",          label: "Feedback" },
    { key: "safety",     path: "/admin/safety",            label: "Safety" },
    { key: "ai-lab",     path: "/admin/ai-lab",            label: "AI Lab" },
    { key: "brain",      path: "/admin/style-brain",       label: "Style Brain" },
    { key: "ref-engine", path: "/admin/reference-engine",  label: "Reference Engine" },
    { key: "founder",    path: "/admin/founder-circle",    label: "Founder Circle" },
    { key: "merchants",  path: "/admin/merchants",         label: "Merchants" },
    { key: "ip-rights",  path: "/admin/ip-rights",         label: "IP & Rights" },
    { key: "merchant-data", path: "/admin/reference-data", label: "Merchant Data" },
    { key: "data-tools", path: "/admin/data-tools",        label: "Data Tools" },
    { key: "payments",   path: "/admin/payments",          label: "Payments" },
    { key: "settings",   path: "/settings",                label: "Settings" },
  ];

  return (
    <nav className="bg-white border-b border-[#E5E4E2] sticky top-0 z-20 overflow-x-auto no-scrollbar" data-testid="admin-sub-nav">
      <div className="max-w-7xl mx-auto px-6 flex items-center h-12 gap-8">
        {links.map(l => (
          <Link key={l.key} to={l.path}
            data-testid={`admin-subnav-${l.key}`}
            className={`text-[10px] uppercase tracking-widest whitespace-nowrap transition-colors ${location.pathname === l.path ? "text-[#C7A96B] font-bold" : "text-[#8E8A84] hover:text-[#0D0D0D]"}`}>
            {l.label}
          </Link>
        ))}
      </div>
    </nav>
  );
};

const SideNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const searchFlag = useFeatureFlag("ui_vision_v4_search");
  const modeNavFlag = useFeatureFlag("mode_based_navigation_v1");
  const { prefs: expPrefs } = useExperienceMode();
  const isAdmin = isAdminUser(user);
  const hasServices = user?.flags?.style_services_v1?.enabled;
  // BottomNav stays strictly 5 tabs. SideNav can add Search for approved
  // users when the flag is on — without ever touching mobile nav.
  // Release-review product fix — Shop BROWSING is public navigation for every
  // member; only checkout is gated behind payment flags (inside the Shop).
  // Mode-Based UX Phase 2 — when the member picked an experience mode, the
  // desktop rail mirrors the mode tab set (plus admin/services extras).
  const modeTabs = (modeNavFlag.on && expPrefs?.configured && Array.isArray(expPrefs.nav_tabs) && expPrefs.nav_tabs.length > 0)
    ? [...expPrefs.nav_tabs]
    : null;
  const sectionsToRender = modeTabs ? [...modeTabs] : [...SECTIONS];
  const hasKey = (k) => sectionsToRender.some(s => s.key === k);
  if (searchFlag.on && !hasKey("search") && !hasKey("discover")) {
    sectionsToRender.push({ key: "search", label: "Search", path: "/search", icon: "search" });
  }
  if (!hasKey("shop")) sectionsToRender.push({ key: "shop", label: "Shop", path: "/shop", icon: "shop" });
  if ((hasServices || isAdmin) && !hasKey("network")) sectionsToRender.push({ key: "services", label: "Style Pros", path: "/marketplace", icon: "services" });
  // Mode-UX Phase 5 — the All-features launcher rides in every mode.
  sectionsToRender.push({ key: "features", label: "All Features", path: "/features", icon: "grid" });
  if (isAdmin) sectionsToRender.push({ key: "admin", label: "Founder Console", path: "/admin/dashboard", icon: "admin" });
  return (
    <aside className="hidden md:flex md:flex-col md:fixed md:top-0 md:left-0 md:h-screen md:w-[200px] border-r border-[#E5E4E2] bg-[#F7F5F1] z-30">
      <div className="p-6"><Logo variant="mark" height={36} /></div>
      <nav className="flex-1 px-3 space-y-1">
        {sectionsToRender.map(s => {
          const active = isActive(s.path, location);
          return (
            <button key={s.key} onClick={() => navigate(s.path, navOpts(location.pathname, s.path))} aria-current={active ? "page" : undefined} data-testid={`sidenav-${s.key}`} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? "bg-white shadow-sm text-[#0D0D0D]" : "text-[#8E8A84] hover:text-[#0D0D0D]"}`}>
              <Glyph name={s.icon} active={active} />
              <span className="font-sans text-[11px] uppercase tracking-widest">{s.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="px-3 pb-4 border-t border-[#E5E4E2] pt-2">
        <ThemeCycleButton />
      </div>
    </aside>
  );
};

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Mode-Based UX Phase 2 — the backend resolves the tab set from the
  // member's experience mode (never more than 5). Falls back to the default
  // tabs when the flag is off or the member hasn't picked a mode yet.
  const modeNavFlag = useFeatureFlag("mode_based_navigation_v1");
  const { prefs } = useExperienceMode();
  const modeTabs = (modeNavFlag.on && prefs?.configured && Array.isArray(prefs.nav_tabs) && prefs.nav_tabs.length > 0)
    ? prefs.nav_tabs.slice(0, 5)
    : null;
  const tabs = modeTabs || [SECTIONS[0], SECTIONS[1], SECTIONS[2], SECTIONS[3],
                { key: "shop", label: "Shop", path: "/shop", icon: "shop" }];
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E5E4E2]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="bottom-nav"
      aria-label="Primary navigation"
    >
      <ul className="flex items-center justify-around h-16 max-w-full">
        {tabs.map(s => {
          const active = isActive(s.path, location);
          return (
            <li key={s.key} className="flex-1 min-w-0">
              <button onClick={() => navigate(s.path, navOpts(location.pathname, s.path))} aria-current={active ? "page" : undefined} className="w-full flex flex-col items-center justify-center gap-1 px-1" data-testid={`bottomnav-${s.key}`}>
                <Glyph name={s.icon} active={active} />
                <span className={`text-[8px] uppercase tracking-widest truncate ${active ? "text-[#0D0D0D]" : "text-[#8E8A84]"}`}>{s.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

// Release-review product fix — persistent avatar (mobile) opens Profile;
// profile/settings live behind it once Shop takes the 5th tab.
const ProfileAvatarButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  if (!user) return null;
  const hidden = ["/login", "/signup", "/beta-gate", "/legal", "/quiz", "/invite"];
  if (location.pathname === "/" || hidden.some(p => location.pathname.startsWith(p))) return null;
  return (
    <button
      type="button"
      onClick={() => navigate("/profile")}
      aria-label="Open your profile"
      data-testid="appshell-avatar-profile"
      className="md:hidden fixed top-3 right-16 z-40 rounded-full shadow-sm">
      <Avatar src={user.picture || user.avatar_url} name={user.name || user.email}
        size="sm" testId="appshell-avatar" />
    </button>
  );
};

// Builder 2 — SecondaryGlassNav (also known as GlassQuickPanel) is an
// app-level secondary navigation layer that auto-hides on scroll. It appears
// across the authenticated app shell (Home / Closet / Stylist / Social /
// Profile) but is suppressed on auth / legal / admin / full-screen flows to
// avoid conflict with those surfaces.
const AppShellGlassNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  if (!user) return null;
  const hiddenPathPrefixes = [
    "/",             // public landing (exact match handled below)
    "/login",
    "/signup",
    "/beta-gate",
    "/beta-access",
    "/verify-email",
    "/verify-id",
    "/reset-password",
    "/legal",         // /legal/accept + /legal/terms + /legal/privacy + ...
    "/privacy",
    "/terms",
    "/ai-disclosure",
    "/data-deletion",
    "/credits",
    "/admin",         // admin surfaces have their own subnav
    "/invite",
    "/quiz",          // full-screen onboarding
  ];
  const path = location.pathname;
  // Exact "/" match — never suppress on any sub-route that starts with "/".
  if (path === "/") return null;
  if (hiddenPathPrefixes.some(p => p !== "/" && (path === p || path.startsWith(p + "/") || path.startsWith(p)))) {
    return null;
  }
  return (
    <div
      className="sticky top-0 z-30 pointer-events-none"
      data-testid="app-shell-glass-nav-wrapper"
    >
      <div className="pointer-events-auto pt-2">
        <GlassQuickPanel />
      </div>
    </div>
  );
};

export const AppShell = ({ children, focus = false }) => (
  <div className="min-h-dvh bg-[#F7F5F1]">
    <SideNav /><AdminSubNav />
    <NotificationBell />
    {!focus && <ProfileAvatarButton />}
    <InstallPrompt />
    <main className={`md:pl-[200px] flex flex-col w-full max-w-full overflow-x-hidden ${focus ? 'h-dvh' : 'min-h-dvh'}`}>
      {!focus && <AppShellGlassNav />}
      {children}
    </main>
    {!focus && <FloatingActionManager />}<BottomNav />
  </div>
);
